import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getCustomersApi, toggleCustomerApi } from '../../api/adminApi';

function tier(spent) {
  if (spent >= 10000) return { label: 'Platinum', bg: '#E8E8F0', fg: '#4338CA' };
  if (spent >= 5000)  return { label: 'Gold',     bg: '#FEF3C7', fg: '#D97706' };
  if (spent >= 1000)  return { label: 'Silver',   bg: '#F3F4F6', fg: '#6B7280' };
  return null;
}

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const LIMIT = 20;

  const load = () => {
    setLoading(true);
    getCustomersApi({ search, page, limit: LIMIT })
      .then((r) => setCustomers(r.data))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, page]); // eslint-disable-line

  const handleToggle = async (id) => {
    try {
      const r = await toggleCustomerApi(id);
      setCustomers((prev) => prev.map((c) => c.id === id ? { ...c, is_active: r.data.is_active } : c));
      toast.success(`Customer ${r.data.is_active ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Failed to update'); }
  };

  const goldCount   = customers.filter((c) => parseFloat(c.total_spent) >= 5000).length;
  const totalSpent  = customers.reduce((sum, c) => sum + parseFloat(c.total_spent || 0), 0);
  const repeatRate  = customers.length
    ? Math.round((customers.filter((c) => parseInt(c.total_orders) > 1).length / customers.length) * 100)
    : 0;

  const shown = filter === 'all' ? customers
    : filter === 'active'   ? customers.filter((c) => c.is_active)
    : filter === 'inactive' ? customers.filter((c) => !c.is_active)
    : filter === 'gold'     ? customers.filter((c) => parseFloat(c.total_spent) >= 5000)
    : customers;

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Customers</h1>
          <p style={S.sub}>{customers.length} customers · {goldCount} Gold+ members</p>
        </div>
      </div>

      {/* KPIs */}
      <div style={S.statGrid}>
        {[
          { label: 'Total Customers', value: customers.length,              icon: '👥', bg: '#EDE9FE', fg: '#6D28D9' },
          { label: 'Gold+ Members',   value: goldCount,                     icon: '⭐', bg: '#FEF3C7', fg: '#D97706' },
          { label: 'Repeat Rate',     value: `${repeatRate}%`,              icon: '🔁', bg: '#DCFCE7', fg: '#16A34A' },
          { label: 'Total Revenue',   value: `₹${totalSpent.toLocaleString('en-IN')}`, icon: '💰', bg: '#FEF9C3', fg: '#854D0E' },
        ].map((s) => (
          <div key={s.label} style={S.statCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={S.statLabel}>{s.label}</p>
                <p style={S.statVal}>{s.value}</p>
              </div>
              <div style={{ ...S.statIcon, background: s.bg, color: s.fg }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.searchWrap}>
          <span style={S.searchIco}>🔍</span>
          <input style={S.searchInp} placeholder="Search by name or email..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div style={S.tabs}>
          {[
            { key: 'all',      label: 'All' },
            { key: 'active',   label: 'Active' },
            { key: 'inactive', label: 'Inactive' },
            { key: 'gold',     label: '⭐ Gold+' },
          ].map((t) => (
            <button key={t.key}
              style={{ ...S.tabBtn, ...(filter === t.key ? S.tabBtnActive : {}) }}
              onClick={() => setFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>{['Customer', 'Email', 'Phone', 'Orders', 'Lifetime Value', 'Tier', 'Joined', 'Status', 'Action'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '48px', color: '#aaa' }}>Loading...</td></tr>
              : shown.length === 0
                ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '48px', color: '#aaa' }}>No customers found</td></tr>
                : shown.map((c) => {
                  const spent   = parseFloat(c.total_spent || 0);
                  const t       = tier(spent);
                  return (
                    <tr key={c.id} style={S.tr}>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={S.avatar}>{c.name?.[0]?.toUpperCase()}</div>
                          <span style={{ fontWeight: 700, color: '#111' }}>{c.name}</span>
                        </div>
                      </td>
                      <td style={S.td}>{c.email}</td>
                      <td style={S.td}>{c.phone || '—'}</td>
                      <td style={S.td}><span style={S.orderCount}>{c.total_orders}</span></td>
                      <td style={S.td}><strong>₹{spent.toLocaleString('en-IN')}</strong></td>
                      <td style={S.td}>
                        {t
                          ? <span style={{ ...S.pill, background: t.bg, color: t.fg }}>{t.label}</span>
                          : <span style={{ fontSize: '12px', color: '#9CA3AF' }}>—</span>
                        }
                      </td>
                      <td style={S.td}>{new Date(c.created_at).toLocaleDateString('en-IN')}</td>
                      <td style={S.td}>
                        <span style={{ ...S.pill, background: c.is_active ? '#DCFCE7' : '#FEE2E2', color: c.is_active ? '#16A34A' : '#DC2626' }}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={S.td}>
                        <button style={{ ...S.toggleBtn, background: c.is_active ? '#FEF3C7' : '#DCFCE7', color: c.is_active ? '#D97706' : '#16A34A' }}
                          onClick={() => handleToggle(c.id)}>
                          {c.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
        <span style={{ fontSize: '13px', color: '#666' }}>Page {page}</span>
        <button style={S.pgBtn} disabled={customers.length < LIMIT} onClick={() => setPage((p) => p + 1)}>Next →</button>
      </div>
    </div>
  );
}

const S = {
  hdr:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' },
  title:     { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:       { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  statGrid:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '18px' },
  statCard:  { background: '#fff', border: '1px solid #E5E5E5', padding: '18px 16px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  statLabel: { fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: '5px' },
  statVal:   { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1px', color: '#111', margin: 0 },
  statIcon:  { width: '38px', height: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' },
  filterBar: { marginBottom: '14px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap:{ position: 'relative', flex: 1, minWidth: '200px' },
  searchIco: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px' },
  searchInp: { width: '100%', border: '1.5px solid #E5E5E5', background: '#fff', padding: '9px 12px 9px 36px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  tabs:      { display: 'flex', border: '1.5px solid #E5E5E5', overflow: 'hidden' },
  tabBtn:    { padding: '7px 14px', border: 'none', background: '#fff', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: '#6B7280', whiteSpace: 'nowrap', borderRight: '1px solid #E5E5E5' },
  tabBtnActive: { background: '#111', color: '#fff' },
  tableWrap: { background: '#fff', border: '1px solid #E5E5E5', overflow: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th:        { padding: '10px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #E5E5E5', textAlign: 'left', background: '#FAFAFA', whiteSpace: 'nowrap' },
  tr:        { borderBottom: '1px solid #F9FAFB', transition: 'background .1s' },
  td:        { padding: '12px 14px', fontSize: '13px', verticalAlign: 'middle' },
  avatar:    { width: '32px', height: '32px', borderRadius: '50%', background: '#111', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '14px', flexShrink: 0 },
  orderCount:{ background: '#EEF2FF', color: '#4338CA', fontSize: '12px', fontWeight: 700, padding: '3px 9px' },
  pill:      { display: 'inline-block', fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '99px' },
  toggleBtn: { border: 'none', padding: '5px 11px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, cursor: 'pointer' },
  pagination:{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '16px' },
  pgBtn:     { background: '#fff', border: '1.5px solid #E5E5E5', padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
};
